/**
 * Las dos piezas del ida y vuelta con el login.
 *
 * La proteccion de rutas de este panel no es un guard por ruta: es el arbol
 * entero (ver `App.tsx`). Sin sesion, las rutas autenticadas **no existen**,
 * asi que no hay forma de renderizar una pantalla protegida ni por un frame,
 * que es el agujero clasico del `if (!session) redirect` puesto adentro de la
 * pantalla.
 *
 * Igual conviene aclarar lo obvio: esto es comodidad, no seguridad. La
 * autoridad es el backend, que responde 401/403 sin importar que dibuje el
 * navegador.
 */

import { Navigate, useLocation } from "react-router-dom";

/** Adonde volver despues de loguearse, guardado en el state de la navegacion. */
interface FromState {
  readonly from?: string;
}

function readFrom(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const from = (state as FromState).from;
  // Solo paths internos: un `from` con origen ("//evil.com") seria un open
  // redirect regalado.
  if (typeof from !== "string" || !from.startsWith("/") || from.startsWith("//")) {
    return null;
  }
  return from;
}

/** Sin sesion: al login, recordando adonde queria ir. */
export function RedirectToLogin() {
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;

  return (
    <Navigate
      to="/login"
      replace
      state={{ from: from === "/login" ? "/" : from } satisfies FromState}
    />
  );
}

/**
 * Con sesion, parado en `/login`: sacarlo de ahi.
 *
 * Pasa siempre que alguien se loguea, porque `LoginPage` no navega -- solo le
 * habla a Supabase, y `AuthProvider` propaga la sesion nueva por
 * `onAuthStateChange`. Este componente es el que cierra el circulo, y ademas
 * cubre el caso de entrar a `/login` con sesion viva.
 */
export function AfterLogin() {
  const location = useLocation();
  return <Navigate to={readFrom(location.state) ?? "/"} replace />;
}
